import { Box } from '@mui/material';

import AddIcon from '@/assets/svg/ui-elements/plus.svg';
import TrashIcon from '@/assets/svg/ui-elements/trash-2.svg';
import { EMAIL_REGEX } from '@/constants/static';
import { IconButton, Input, Select, type SelectOption, Typography } from '@/design-system';
import { formatWithoutFraction } from '@/utils/numbers/formatters';
import { onlyNumbersWithoutDigits } from '@/utils/numbers/helpers';

import { SHAREHOLDER_FIELDS, SHAREHOLDER_TYPE_OPTIONS } from './share-structure.constants';
import {
  AddShareholderButton,
  ChangedOutline,
  ShareholderGroup,
  ShareholderRow,
  ShareholderRowCell,
} from './share-structure.styled';
import { useDeferredRemove } from './use-deferred-remove';
import { useStableKeys } from './use-stable-keys';

type BaseShareholderField = 'type' | 'email' | 'fullName' | 'numberOfShares';

type TemplateShareholder = {
  type: string | null;
  email: string | null;
  fullName: string | null;
  numberOfShares: number | null;
  [key: string]: unknown;
};

type TemplateShareClass = {
  numberOfShares: number | null;
  shareholders: TemplateShareholder[] | null;
  [key: string]: unknown;
};

interface ShareholderListProps<T extends TemplateShareClass = TemplateShareClass> {
  shareClass: T;
  classIdx: number;
  disabled?: boolean;
  isStepValidated: boolean;
  fieldErrors: Record<string, unknown>;
  shareholderOptions?: readonly SelectOption[];
  onUpdateShareholder: (
    _classIdx: number,
    _shareholderIdx: number,
    _field: BaseShareholderField,
    _value: string,
  ) => void;
  onAddShareholder: (_classIdx: number) => void;
  onRemoveShareholder: (_classIdx: number, _shareholderIdx: number) => void;
  changedColor?: string;
  isChanged?: (_path: string) => boolean;
  getShareholderPath?: (_classIdx: number, _shareholderIdx: number) => string;
}

export const ShareholderList = <T extends TemplateShareClass>({
  shareClass,
  classIdx,
  disabled = false,
  isStepValidated,
  fieldErrors,
  shareholderOptions = SHAREHOLDER_TYPE_OPTIONS,
  onUpdateShareholder,
  onAddShareholder,
  onRemoveShareholder,
  changedColor,
  isChanged,
  getShareholderPath,
}: ShareholderListProps<T>) => {
  const classTotal = Number(shareClass?.numberOfShares ?? 0);
  const { keys, removeKeyAt } = useStableKeys(
    shareClass.shareholders?.length ?? 0,
    `shareholder-${classIdx}`,
  );
  const { exitingIndex, requestRemove } = useDeferredRemove((index) => {
    removeKeyAt(index);
    onRemoveShareholder(classIdx, index);
  });

  return (
    <ShareholderGroup>
      {(shareClass.shareholders ?? []).map((s, shareholderIdx) => {
        const n =
          s.numberOfShares == null || Number.isNaN(Number(s.numberOfShares))
            ? null
            : Number(s.numberOfShares);

        const percent = classTotal > 0 && n != null ? ((n / classTotal) * 100).toFixed(2) : '-';

        const percentAdornment =
          percent === '-' ? null : <Typography variant="body3">%</Typography>;

        const basePath =
          getShareholderPath?.(classIdx, shareholderIdx) ??
          `numberUnits.shareClasses[${classIdx}].shareholders[${shareholderIdx}]`;

        const typeChanged = isChanged?.(`${basePath}.type`) ?? false;

        return (
          <ShareholderRow
            key={keys[shareholderIdx]}
            data-testid={`shareholder-row-${classIdx}-${shareholderIdx}`}
            $exiting={exitingIndex === shareholderIdx}
          >
            <ChangedOutline $color={typeChanged ? changedColor : undefined}>
              <Select
                size="lg"
                label="Type"
                required
                disabled={disabled}
                value={s.type || null}
                options={[...shareholderOptions]}
                onChange={(value) => onUpdateShareholder(classIdx, shareholderIdx, 'type', value)}
                state={
                  fieldErrors[`shareholders.${classIdx}.${shareholderIdx}.type`] && isStepValidated
                    ? 'destructive'
                    : 'default'
                }
                placeholder="Select Type"
              />
            </ChangedOutline>

            {SHAREHOLDER_FIELDS.map((field) => {
              const fieldName = field.name;
              const isShares = fieldName === 'numberOfShares';
              const value =
                isShares && s.numberOfShares != null
                  ? formatWithoutFraction(s.numberOfShares)
                  : s[fieldName];
              const key = `shareholders.${classIdx}.${shareholderIdx}.${field.name}`;
              const isEmail = field.name === 'email';
              const emailValue = String(value ?? '');
              const isInvalidEmail =
                isEmail && emailValue.trim().length > 0 && !EMAIL_REGEX.test(emailValue.trim());
              const hasError = isInvalidEmail || (!!fieldErrors[key] && isStepValidated);
              const fieldChanged = isChanged?.(`${basePath}.${fieldName}`) ?? false;

              return (
                <Box key={field.name}>
                  <ChangedOutline $color={fieldChanged ? changedColor : undefined}>
                    <Input
                      size="lg"
                      label={field.label}
                      required
                      disabled={disabled}
                      placeholder={field.placeholder}
                      value={value || ''}
                      type={isEmail ? 'email' : undefined}
                      maxLength={100}
                      state={hasError ? 'destructive' : 'default'}
                      helperText={isEmail && isInvalidEmail ? 'Invalid email' : ''}
                      onChange={(e) => {
                        let raw = e.target.value;
                        if (isShares) raw = onlyNumbersWithoutDigits(raw);
                        onUpdateShareholder(classIdx, shareholderIdx, fieldName, raw);
                      }}
                    />
                  </ChangedOutline>
                </Box>
              );
            })}

            <ShareholderRowCell>
              <Input
                size="lg"
                aria-label="Percent of class"
                placeholder="-"
                disabled
                value={percent}
                endIcon={percentAdornment}
              />
            </ShareholderRowCell>

            <ShareholderRowCell>
              <IconButton
                aria-label="Remove shareholder"
                variant="outline"
                color="destructive"
                size="sm"
                disabled={disabled || exitingIndex !== null}
                onClick={() => requestRemove(shareholderIdx)}
              >
                <TrashIcon />
              </IconButton>
            </ShareholderRowCell>
          </ShareholderRow>
        );
      })}

      <AddShareholderButton
        variant="subtle"
        color="primary"
        size="md"
        onClick={() => onAddShareholder(classIdx)}
        disabled={disabled}
      >
        <AddIcon />
        Add Shareholder
      </AddShareholderButton>
    </ShareholderGroup>
  );
};
